'use client'

/**
 * Hook brief → post-signup chat handoff for /tools/ai-hook-generator.
 *
 * Mode B: nothing is generated here. The visitor describes the product, the
 * audience and the placement; that is composed into one chat message and
 * staged in `pending-prompt-store` (sessionStorage, same tab) — the store the
 * public ad library uses to carry a remix prompt through sign-up.
 *
 * The return trip reuses `/go/ad-library-remix`, which despite its name is a
 * generic "signed-in → preferred brand's chat/new" router: unauthenticated →
 * /sign-up, no org → /onboarding (allowlisted in onboarding-return-path, so a
 * first-time signup comes back to it after the wizard instead of landing in
 * the kickoff chat), onboarded → `/o/{org}/b/{brand}/chat/new`. chat/new reads
 * the staged prompt and, because no launch source is attached
 * (`resolvePendingPromptLaunchAction(null)` → 'prefill'), puts it in the
 * composer as a draft. It is never auto-sent.
 *
 * This is local rather than a change to the shared ModeBBriefCapture, which
 * still copies the brief by hand; folding this handoff into it is the
 * follow-up that upgrades every Mode B page at once.
 */

import { useCallback, useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'

import { ToolAuthDialog } from '@/components/marketing/tools/tool-auth-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { useAuthContext } from '@/hooks/use-auth-context'
import { track } from '@/lib/analytics/track'
import { usePendingPromptStore } from '@/stores/pending-prompt-store'

const PAGE_PATH = '/tools/ai-hook-generator'
const CHAT_HANDOFF_PATH = '/go/ad-library-remix'
const TRACKING_SLUG = 'ai-hook-generator'
/** One PostHog flush interval plus headroom — see ad-library-experience. */
const POSTHOG_FLUSH_GRACE_MS = 400

const PLATFORMS = [
  'TikTok',
  'Instagram Reels',
  'Facebook feed',
  'YouTube Shorts',
] as const
type Platform = (typeof PLATFORMS)[number]

const OUTPUTS = {
  both: 'Text hooks + opening shot',
  text: 'Text hooks only',
  visual: 'Opening shot only',
} as const
type OutputKind = keyof typeof OUTPUTS

export function composeHookPrompt({
  product,
  audience,
  platform,
  output,
}: {
  product: string
  audience: string
  platform: Platform
  output: OutputKind
}): string {
  const lines = [
    `Write ad hooks for this ${platform} ad.`,
    '',
    `What I'm advertising: ${product.trim()}`,
  ]
  if (audience.trim()) lines.push(`Who it's for: ${audience.trim()}`)
  lines.push('')
  if (output !== 'visual') {
    lines.push(
      'Give me 10 text hooks (spoken line + on-screen text, under 12 words each), spread across different angles — problem, result, curiosity, objection, proof, contrarian — and label the angle on each.',
    )
  }
  if (output !== 'text') {
    lines.push(
      output === 'both'
        ? 'For the 3 strongest, plan the first 3 seconds shot by shot: the opening frame, what moves, the on-screen text, and whether it still works with the sound off.'
        : 'Plan 3 different first-3-second openings shot by shot: the opening frame, what moves, the on-screen text, and whether it still works with the sound off.',
    )
  }
  lines.push(
    `Keep it native to ${platform} — no logo intro, the product or the problem on screen in the first second.`,
  )
  return lines.join('\n')
}

export function HookBriefForm() {
  const router = useRouter()
  const { isLoaded, isSignedIn } = useAuthContext()
  const [product, setProduct] = useState('')
  const [audience, setAudience] = useState('')
  const [platform, setPlatform] = useState<Platform>('TikTok')
  const [output, setOutput] = useState<OutputKind>('both')
  const [showError, setShowError] = useState(false)
  const [authOpen, setAuthOpen] = useState(false)

  const prompt = useMemo(
    () => composeHookPrompt({ product, audience, platform, output }),
    [product, audience, platform, output],
  )
  const signedIn = isLoaded && isSignedIn
  const ctaText = signedIn ? 'Open in Soku chat' : 'Continue — free account'

  const submit = useCallback(async () => {
    if (!product.trim()) {
      setShowError(true)
      return
    }
    usePendingPromptStore
      .getState()
      .setPendingPrompt(prompt, [], [], crypto.randomUUID(), null)
    track('tool_detail_cta_click', {
      slug: TRACKING_SLUG,
      cta_location: 'hero_brief',
      cta_text: ctaText,
      platform,
      output,
      has_audience: Boolean(audience.trim()),
    })

    if (signedIn) {
      await new Promise(resolve => setTimeout(resolve, POSTHOG_FLUSH_GRACE_MS))
      window.location.assign(CHAT_HANDOFF_PATH)
      return
    }
    // SelfAuthForm reads `redirect_url` from the page URL and assigns it on
    // success, so set it before the dialog opens.
    router.replace(
      `${PAGE_PATH}?redirect_url=${encodeURIComponent(CHAT_HANDOFF_PATH)}`,
      { scroll: false },
    )
    setAuthOpen(true)
  }, [audience, ctaText, output, platform, product, prompt, router, signedIn])

  return (
    <div
      id="studio"
      className="scroll-mt-24 rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-left sm:p-7"
    >
      <form
        onSubmit={event => {
          event.preventDefault()
          void submit()
        }}
        className="space-y-5"
      >
        <div className="space-y-2">
          <Label htmlFor="hook-product">What are you advertising?</Label>
          <Textarea
            id="hook-product"
            value={product}
            onChange={event => {
              setProduct(event.target.value)
              if (showError) setShowError(false)
            }}
            placeholder="e.g. A $29 posture corrector that is invisible under a shirt. The offer: 30-day free returns."
            rows={3}
            className="resize-y"
            aria-invalid={showError || undefined}
            aria-describedby={showError ? 'hook-product-error' : undefined}
          />
          {showError ? (
            <p id="hook-product-error" className="text-sm text-orange-400">
              Add the product or offer first — the hooks are written around it.
            </p>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="hook-audience">
            Who is it for?{' '}
            <span className="font-normal text-white/40">(optional)</span>
          </Label>
          <Input
            id="hook-audience"
            value={audience}
            onChange={event => setAudience(event.target.value)}
            placeholder="e.g. Remote workers, 25–45, who already have back pain"
          />
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium">Where will it run?</p>
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            spacing={2}
            value={platform}
            onValueChange={value => {
              if (value) setPlatform(value as Platform)
            }}
            className="flex flex-wrap"
            aria-label="Placement"
          >
            {PLATFORMS.map(item => (
              <ToggleGroupItem
                key={item}
                value={item}
                className="rounded-full px-3"
              >
                {item}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium">What do you need?</p>
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            spacing={2}
            value={output}
            onValueChange={value => {
              if (value) setOutput(value as OutputKind)
            }}
            className="flex flex-wrap"
            aria-label="Output"
          >
            {(Object.keys(OUTPUTS) as OutputKind[]).map(key => (
              <ToggleGroupItem
                key={key}
                value={key}
                className="rounded-full px-3"
              >
                {OUTPUTS[key]}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        {product.trim() ? (
          <div className="rounded-xl border border-white/10 bg-black/30 p-4">
            <p className="text-xs font-semibold tracking-[0.14em] text-white/40 uppercase">
              Your first message to Soku
            </p>
            <p className="mt-2 text-sm leading-6 whitespace-pre-wrap text-white/70">
              {prompt}
            </p>
          </div>
        ) : null}

        <div className="space-y-3">
          <Button
            type="submit"
            className="w-full rounded-full bg-orange-500 font-semibold text-stone-950 hover:bg-orange-400 sm:w-auto sm:px-6"
          >
            {ctaText}
          </Button>
          <p className="text-sm leading-6 text-white/45">
            {signedIn
              ? 'Opens a new chat in your brand with this message filled in. Review it, then send.'
              : 'Nothing is generated on this page. After you sign up, this message is waiting in a new Soku chat — review it, then send.'}
          </p>
        </div>
      </form>

      <ToolAuthDialog
        open={authOpen}
        onOpenChange={setAuthOpen}
        title="Create your Soku account"
        description="Your hook brief is saved. Sign up and it opens as your first message in Soku's chat."
      />
    </div>
  )
}
